/**
 * @module       utils
 * @description  Helper functions.
 *
 * @author  William Huster <william@aspire.is>
 */
import { debounce } from './debounce'
import { debug } from './debug'

export default {
  debounce,
  debug,
  toSnakeCase,
  toCamelCase,
  objectToCamelCase,
  objectToSnakeCase,
  isObject,
  generateLocation,
  generateUserLocation,
  toUpperFirst,
  sortDataByField,
  isDefined,
}

/**
 * Transform a string value from `camelCase` style notation to `snake_case` notation.
 * This is useful for translating Python-serialized JSON objects to JavaScript objects.
 *
 * @param {String} value - The string value to transform
 */
export function toSnakeCase(value) {
  let upperChars = value.match(/([A-Z])/g)

  if (!upperChars) {
    return value
  }

  let str = value.toString()
  for (let i = 0, n = upperChars.length; i < n; i++) {
    str = str.replace(new RegExp(upperChars[i]), '_' + upperChars[i].toLowerCase())
  }

  if (str.slice(0, 1) === '_') {
    str = str.slice(1)
  }

  return str
}

/**
 * Transform a string value from `snake_case` style notation to `camelCase` notation.
 * This is useful for translating Python-serialized JSON objects to JavaScript objects.
 *
 * @param {String} value - The string value to transform.
 */
export function toCamelCase(value) {
  return value
    .split('_')
    .map(function(word, index) {
      // Do nothing with the first word
      if (index === 0) {
        return word
      }
      // If it is not the first word only upper case the first char and lowercase the rest.
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    })
    .join('')
}

/**
 * Check whether a given value is an object.
 */
export function isObject(value) {
  return value !== null && value instanceof Object && !Array.isArray(value)
}

/**
 * Transform the string-based keys of a JavaScript object to `camelCase` style notation.
 * This is useful for translating the style of object keys after making an API call to
 * the Python-based API, which uses `snake_case` style notation by default.
 */
export function objectToCamelCase(value) {
  if (isObject(value)) {
    return Object.keys(value).reduce((acc, snakeKey) => {
      const camelKey = toCamelCase(snakeKey)
      acc[camelKey] = isObject(value[snakeKey])
        ? objectToCamelCase(value[snakeKey])
        : value[snakeKey]
      return acc
    }, {})
  }
}

/**
 * Transform the string-based keys of a JavaScript object to `snake_case` style notation.
 * This is useful for translating the `camelCase` style of JavaScript keys BEFORE posting
 * the data to the Python-based API, which uses `snake_case` style notation by default.
 */
export function objectToSnakeCase(value) {
  if (isObject(value)) {
    return Object.keys(value).reduce((acc, camelKey) => {
      const snakeKey = toSnakeCase(camelKey)
      acc[snakeKey] = isObject(value[camelKey])
        ? objectToSnakeCase(value[camelKey])
        : value[camelKey]
      return acc
    }, {})
  }
}

/**
 * Transform a first char at string value from `lower case` to `Lower case`.
 * This is useful for capitalize first letter in word or sentence
 */
export function toUpperFirst(value) {
  return value.charAt(0).toUpperCase() + value.slice(1)
}

/**
 * Sort a list of objects by the values of a given attribute.
 *
 * @todo - Rewrite this function without side effects. The current
 *         implementation will modify the list represented by `data`
 *         in place, which may not be the intent.
 *
 * @param {Array<Object>} data - List of objects to be sorted.
 * @param {String} field - Name of the attribute to sort by.
 *
 * @returns {Array<Object>} - The sorted list.
 */
export function sortDataByField(data, field) {
  data.sort(function(a, b) {
    if (a[field] > b[field]) {
      return 1
    }
    if (a[field] < b[field]) {
      return -1
    }
    return 0
  })
  return data
}

/**
 * Format an address object as a string.
 * This function places commas and spaces properly, even in cases where
 * certain attributes of the address are not present.
 *
 * @todo - Rename this function to `formatAddress`
 *
 * @param {Object} address - And `Address` object containing attirbutes of the address.
 *
 * @returns {String} -
 */
export function generateLocation(address) {
  return [
    [address.address1, address.address2, address.city].filter(i => i.length).join(' '),
    [address.state, address.zipcode].filter(i => i.length).join(' '),
  ]
    .filter(i => i.length)
    .join(', ')
}

/**
 * Alternate format for a user location.
 * This function places commas and spaces properly, even in cases where
 * certain attributes of the address are not present.
 *
 * @todo - Give this function a better name.
 *
 * @param {Object} address - And `Address` object containing attirbutes of the address.
 *
 * @returns {String} -
 */
export function generateUserLocation(address) {
  return [
    [address.city].filter(i => i.length).join(' '),
    [address.state, address.zipcode].filter(i => i.length).join(' '),
  ]
    .filter(i => i.length)
    .join(', ')
}

/**
 * Check whether a value is defined.
 **/
export function isDefined(value) {
  return typeof value !== 'undefined'
}
